import React from "react"
import PropTypes from "prop-types"
import { Table } from "reactstrap"
import { withTranslation } from "react-i18next"
import { naturezaDe } from "./plano-contas-arvore"

const ESTILO_NATUREZA = {
  R: { background: "#dcf3ea", borderColor: "#b9e4d2", color: "#1d6e50", icone: "bx bx-money" },
  D: { background: "#fde8e8", borderColor: "#f3c4c4", color: "#a12b2b", icone: "bx bx-receipt" },
}

// Ações no padrão das listas do sistema: ícone mdi colorido, sem moldura de botão
const BotaoAcao = ({ icone, cor, rotulo, titulo, desabilitado, onClick }) => (
  <button type="button" className={ `btn btn-link p-0 me-3 ${ cor }` } aria-label={ rotulo } title={ titulo }
          disabled={ desabilitado } onClick={ onClick }>
    <i className={ `mdi ${ icone } font-size-18` } />
  </button>
)

BotaoAcao.propTypes = {
  icone: PropTypes.string.isRequired,
  cor: PropTypes.string.isRequired,
  rotulo: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
  desabilitado: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
}

const PlanoContasGrade = props => {
  const { linhas, grupos, recolhidos, erros, permissoes, temAgrupadoras, onAlterar, onAlterarNatureza, onAlternarRecolher,
    onAlternarTodas, onMover, onAdicionarSubconta, onEnterDescricao, onAvancado, onExcluir, t } = props

  const fundoDaLinha = linha => (linha.novo ? "#f3f5fd" : linha.alterado ? "#fffaf0" : undefined)
  const algumaRecolhida = recolhidos.size > 0

  return (
    <div className="table-responsive">
      <Table striped bordered className="dataTable align-middle" style={ { minWidth: 960 } }>
        <thead>
          <tr>
            <th style={ { width: 130 } }>{ t("planocontas.grade.codigo") }</th>
            <th>
              <div className="d-flex align-items-center gap-2">
                <span style={ { flex: "0 0 24px", width: 24 } }>
                  { temAgrupadoras && (
                    <button type="button" className="btn btn-link p-0 text-secondary"
                            aria-label={ t(algumaRecolhida ? "planocontas.expandirTudo" : "planocontas.recolherTudo") }
                            title={ t(algumaRecolhida ? "planocontas.expandirTudo" : "planocontas.recolherTudo") }
                            onClick={ onAlternarTodas }>
                      <i className={ `mdi ${ algumaRecolhida ? "mdi-unfold-more-horizontal" : "mdi-unfold-less-horizontal" } font-size-18` } />
                    </button>
                  ) }
                </span>
                { t("planocontas.grade.descricao") }
              </div>
            </th>
            <th style={ { width: 210 } }>{ t("planocontas.grade.natureza") }</th>
            <th style={ { width: 260 } }>{ t("planocontas.grade.dre") }</th>
            <th style={ { width: 70 } } className="text-end">{ t("planocontas.grade.lancamentos") }</th>
            <th style={ { width: 170 } }>{ t("planocontas.grade.acoes") }</th>
          </tr>
        </thead>
        <tbody>
          { linhas.map(linha => {
            const natureza = naturezaDe(linha.tipo)
            const estilo = ESTILO_NATUREZA[natureza]
            const erro = erros[linha.chave]
            const podeEditar = linha.novo ? permissoes.inserir : permissoes.editar
            return (
              <tr key={ linha.chave } style={ { background: fundoDaLinha(linha) } }>
                <td className="text-start">
                  <div className="d-flex align-items-center gap-2">
                    <span className="font-monospace" style={ { fontWeight: linha.temFilhas ? 600 : 400 } }>{ linha.codigo }</span>
                    { linha.novo && <span className="badge badge-soft-primary">{ t("planocontas.grade.nova") }</span> }
                    { linha.alterado && <span title={ t("planocontas.grade.alterada") }
                                              style={ { width: 8, height: 8, borderRadius: "50%", background: "#c98a12", display: "inline-block" } } /> }
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <span style={ { flex: "0 0 24px", width: 24, height: 24 } }>
                      { linha.temFilhas && (
                        <button type="button" className="btn btn-link p-0 text-secondary"
                                aria-label={ t(recolhidos.has(linha.codigo) ? "planocontas.grade.expandir" : "planocontas.grade.recolher", { codigo: linha.codigo }) }
                                onClick={ () => onAlternarRecolher(linha.codigo) }>
                          <i className={ `mdi ${ recolhidos.has(linha.codigo) ? "mdi-chevron-right" : "mdi-chevron-down" } font-size-18` } />
                        </button>
                      ) }
                    </span>
                    <div className="flex-grow-1">
                      <input id={ `descricao-${ linha.chave }` }
                             className={ "form-control" + (erro ? " is-invalid" : "") }
                             aria-label={ t("planocontas.grade.descricao.aria", { codigo: linha.codigo }) }
                             placeholder={ t("planocontas.grade.descricao.placeholder") }
                             maxLength={ 50 }
                             value={ linha.descricao }
                             disabled={ !podeEditar }
                             style={ { fontWeight: linha.temFilhas ? 600 : 400 } }
                             onChange={ e => onAlterar(linha.chave, { descricao: e.target.value }) }
                             onKeyDown={ e => { if (e.key === "Enter") onEnterDescricao(linha) } } />
                      { erro && <div className="invalid-feedback d-block">{ erro }</div> }
                    </div>
                  </div>
                </td>
                <td>
                  <div className="btn-group" role="group" aria-label={ t("planocontas.grade.natureza.aria", { codigo: linha.codigo }) }>
                    { ["R", "D"].map(opcao => {
                      const ativa = natureza === opcao
                      const estiloOpcao = ESTILO_NATUREZA[opcao]
                      return (
                        <button key={ opcao } type="button" className="btn btn-sm d-flex align-items-center gap-1"
                                aria-pressed={ ativa }
                                title={ linha.temFilhas ? t("planocontas.grade.natureza.title") : undefined }
                                disabled={ !podeEditar }
                                onClick={ () => { if (!ativa) onAlterarNatureza(linha, opcao) } }
                                style={ {
                                  border: `1px solid ${ ativa ? estiloOpcao.borderColor : "#ced4da" }`,
                                  background: ativa ? estiloOpcao.background : "#fff",
                                  color: ativa ? estiloOpcao.color : "#74788d",
                                  fontWeight: ativa ? 600 : 400,
                                } }>
                          <i className={ `${ estiloOpcao.icone } font-size-16` } aria-hidden="true" />
                          { t(`planocontas.natureza.${ opcao }`) }
                        </button>
                      )
                    }) }
                  </div>
                </td>
                <td>
                  <select className="form-select"
                          aria-label={ t("planocontas.grade.dre.aria", { codigo: linha.codigo }) }
                          value={ String(linha.dre || 0) }
                          disabled={ !podeEditar }
                          onChange={ e => onAlterar(linha.chave, { dre: Number(e.target.value) }) }>
                    <option value="0">{ t("planocontas.grade.dre.semGrupo") }</option>
                    { grupos.map(grupo => (
                      <option key={ grupo.idId } value={ String(grupo.idId) }>{ `${ grupo.idId } · ${ grupo.descricao }` }</option>
                    )) }
                  </select>
                </td>
                <td className="text-end" style={ { fontVariantNumeric: "tabular-nums" } }>
                  { linha.temFilhas ? "—" : (linha.lancamentos || 0).toLocaleString("pt-BR") }
                </td>
                <td className="text-nowrap">
                  <BotaoAcao icone="mdi-arrow-up" cor="text-secondary"
                             rotulo={ t("planocontas.grade.subir", { codigo: linha.codigo }) } titulo={ t("planocontas.grade.subir.title") }
                             desabilitado={ !permissoes.editar || linha.primeiraIrma } onClick={ () => onMover(linha.codigo, "subir") } />
                  <BotaoAcao icone="mdi-arrow-down" cor="text-secondary"
                             rotulo={ t("planocontas.grade.descer", { codigo: linha.codigo }) } titulo={ t("planocontas.grade.descer.title") }
                             desabilitado={ !permissoes.editar || linha.ultimaIrma } onClick={ () => onMover(linha.codigo, "descer") } />
                  <BotaoAcao icone="mdi-plus-circle-outline" cor="text-success"
                             rotulo={ t("planocontas.grade.subconta", { codigo: linha.codigo }) } titulo={ t("planocontas.grade.subconta.title") }
                             desabilitado={ !permissoes.inserir || !linha.podeTerSubconta } onClick={ () => onAdicionarSubconta(linha) } />
                  <BotaoAcao icone="mdi-tune" cor="text-primary"
                             rotulo={ t("planocontas.grade.avancado", { codigo: linha.codigo }) } titulo={ t("planocontas.grade.avancado.title") }
                             onClick={ () => onAvancado(linha.chave) } />
                  <BotaoAcao icone="mdi-delete" cor="text-danger"
                             rotulo={ t("planocontas.grade.excluir", { codigo: linha.codigo }) } titulo={ t("planocontas.grade.excluir.title") }
                             desabilitado={ !permissoes.excluir } onClick={ () => onExcluir(linha) } />
                </td>
              </tr>
            )
          }) }
          { linhas.length === 0 && (
            <tr>
              <td colSpan={ 6 } className="text-center">{ t("planocontas.grade.vazia") }</td>
            </tr>
          ) }
        </tbody>
      </Table>
    </div>
  )
}

PlanoContasGrade.propTypes = {
  linhas: PropTypes.array.isRequired,
  grupos: PropTypes.array.isRequired,
  recolhidos: PropTypes.object.isRequired,
  erros: PropTypes.object.isRequired,
  permissoes: PropTypes.object.isRequired,
  temAgrupadoras: PropTypes.bool,
  onAlterar: PropTypes.func.isRequired,
  onAlterarNatureza: PropTypes.func.isRequired,
  onAlternarRecolher: PropTypes.func.isRequired,
  onAlternarTodas: PropTypes.func.isRequired,
  onMover: PropTypes.func.isRequired,
  onAdicionarSubconta: PropTypes.func.isRequired,
  onEnterDescricao: PropTypes.func.isRequired,
  onAvancado: PropTypes.func.isRequired,
  onExcluir: PropTypes.func.isRequired,
  t: PropTypes.func,
}

export default withTranslation()(PlanoContasGrade)
